export type InsightType = 'dormant' | 'flight' | 'smb' | 'relgap';
export type InsightLabel = 'Flight Risk' | 'Dormant' | 'SMB Growth' | 'Rel Gap';
export type SeverityLevel = 'HIGH' | 'MED';

/** One evaluated ICP criterion for a specific customer × product pairing. */
export interface IcpCriterionResult {
  /** Human-readable rule label from product.rules[n].label */
  label: string;
  /** True = customer meets this criterion */
  met: boolean;
  /**
   * The customer's actual value for the field this rule inspects, formatted for
   * display in parentheses next to the label (SCRUM-275). E.g. a balance rule
   * yields "$84K"; a tenure rule yields "18 mo". `undefined` when the rule has
   * no meaningful scalar to show (boolean holding-presence rules, etc.). Values
   * are real customer data from `/api/customers` (PostgreSQL via bankiq-api).
   *
   * Typed `string | undefined` (not just optional) because the codebase runs
   * `exactOptionalPropertyTypes`, and `buildInsightRows` assigns the result of
   * `annotateCriterion()` (which may be `undefined`) directly to this key.
   */
  annotation?: string | undefined;
}

export interface InsightRow {
  customerId: string;
  customerName: string;
  insightType: InsightType;
  insightLabel: InsightLabel;
  severity: SeverityLevel;
  /** Hex color string for the severity indicator bar */
  severityColor: '#EF4444' | '#FB923C';
  /** Met-criteria count against the best-matching product (see buildInsightRows). */
  icpMatchCount: number;
  ageDays: number;
  /** Evaluated ICP criteria for the best-matching product for this insight. */
  icpCriteria: IcpCriterionResult[];
  /** Total number of criteria (= icpCriteria.length, cached for the pill label). */
  icpTotal: number;
  /** Name of the best-matching product (popover title). */
  icpProductName: string;
}

/**
 * UI insight type → backend wire integer (the Api's InsightType enum).
 * Relocated from useInsightSummaries.ts when the pre-v6 Intelligence
 * Dashboard was retired (SCRUM-323); the campaigns AI hooks
 * (useInsightExplanation) remain the consumer.
 */
export const INSIGHT_TYPE_TO_WIRE: Record<InsightType, number> = {
  dormant: 0,
  flight: 1,
  relgap: 2,
  smb: 3,
};
